export const UNIT = /** @type {const} */ ({
	inches: 'inches',
	millimeters: 'millimeters',
	centimeters: 'centimeters',
	meters: 'meters',
})
/** @typedef {typeof UNIT[keyof typeof UNIT]} Unit */

export const UNIT_EXPONENT = /** @type {const} */ ({
	none: 'none',
	square: 'square',
	cubic: 'cubic',
})
/** @typedef {typeof UNIT_EXPONENT[keyof typeof UNIT_EXPONENT]} UnitExponent */

/** @type {Record<Unit, number>} */
export const LENGTH_IN_METERS = {
	[UNIT.inches]: 0.0254,
	[UNIT.millimeters]: 0.001,
	[UNIT.centimeters]: 0.01,
	[UNIT.meters]: 1,
}

/** @type {Record<UnitExponent, number>} */
export const DIMENSION_EXPONENTS = {
	[UNIT_EXPONENT.none]: 1,
	[UNIT_EXPONENT.square]: 2,
	[UNIT_EXPONENT.cubic]: 3,
}

/**
 * @param {number} value
 * @param {Unit} from
 * @param {Unit} to
 * @param {UnitExponent} [unitExponent]
 * @returns {number}
 */
export const convertUnit = (value, from, to, unitExponent = UNIT_EXPONENT.none) => {
	const exponent = DIMENSION_EXPONENTS[unitExponent]
	return value * Math.pow(LENGTH_IN_METERS[from] / LENGTH_IN_METERS[to], exponent)
}

/**
 * @param {{ boundaryBoxVolume: number|undefined, volume: number|undefined }} args
 * @returns {number}
 */
export const calculateChipVolume = ({ boundaryBoxVolume, volume }) => {
	if (boundaryBoxVolume == null || volume == null) return 0
	return boundaryBoxVolume - volume
}

/**
 * @param {{ size?: { x?: number, y?: number, z?: number } }} args
 * @returns {number}
 */
export const calculateBoundaryBoxVolume = ({ size: { x, y, z } = {} }) => {
	if (x == null || y == null || z == null) return 0
	return x * y * z
}

/**
 * @param {{
 *   size:   { value?: { x?: number, y?: number, z?: number }, unit: Unit } | undefined,
 *   area:   { value?: number, unit: Unit } | undefined,
 *   volume: { value?: number, unit: Unit } | undefined,
 * }} args
 */
export const getPartMeasures = ({ size, area, volume }) => {
	const size_mm = {
		x: convertUnit(size.value.x, size.unit, UNIT.millimeters),
		y: convertUnit(size.value.y, size.unit, UNIT.millimeters),
		z: convertUnit(size.value.z, size.unit, UNIT.millimeters),
	}
	const area_cm2 = convertUnit(area.value, area.unit, UNIT.centimeters, UNIT_EXPONENT.square)
	const volume_cm3 = convertUnit(volume.value, volume.unit, UNIT.centimeters, UNIT_EXPONENT.cubic)

	const boundaryBoxVolume_mm3 = calculateBoundaryBoxVolume({ size: size_mm })
	const boundaryBoxVolume_cm3 = convertUnit(boundaryBoxVolume_mm3, UNIT.millimeters, UNIT.centimeters, UNIT_EXPONENT.cubic)
	const chipVolume_cm3 = calculateChipVolume({ volume: volume_cm3, boundaryBoxVolume: boundaryBoxVolume_cm3 })

	return {
		size_mm: {
			x: Number(size_mm.x.toFixed(2)),
			y: Number(size_mm.y.toFixed(2)),
			z: Number(size_mm.z.toFixed(2)),
		},
		area_cm2: 				Number(area_cm2.toFixed(2)),
		volume_cm3: 			Number(volume_cm3.toFixed(2)),
		boundaryBoxVolume_cm3:	Number(boundaryBoxVolume_cm3.toFixed(2)),
		chipVolume_cm3: 		Number(chipVolume_cm3.toFixed(2)),
	}
}
